import { useEffect, useState } from 'react';
import {
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  Area,
  AreaChart,
} from 'recharts';
import { api } from '@/lib/api';
import { useMonthStore } from '@/lib/monthStore';
import { GlassCard } from '@/components/ui/GlassCard';
import { PageHeader } from '@/components/ui/PageHeader';
import { EmptyState } from '@/components/ui/EmptyState';
import { SkuStatusBadge } from '@/components/ui/StatusBadge';
import { formatNumber, formatDate } from '@/lib/format';
import type { ForecastPoint, PmixItem } from '@/types';

interface SkuForecast {
  code: string;
  name: string;
  historicalAvg: number;
  forecastNext7: number[];
}

export default function Trends() {
  const selectedMonthId = useMonthStore((s) => s.selectedMonthId);
  const [forecast, setForecast] = useState<ForecastPoint[]>([]);
  const [skuForecasts, setSkuForecasts] = useState<SkuForecast[]>([]);
  const [pmix, setPmix] = useState<PmixItem[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!selectedMonthId) return;
    setLoading(true);
    Promise.all([
      api.get<ForecastPoint[]>(`/forecast/month/${selectedMonthId}?days=7`),
      api.get<SkuForecast[]>(`/forecast/month/${selectedMonthId}/skus?days=7`),
      api.get<PmixItem[]>(`/sales/month/${selectedMonthId}/pmix`),
    ])
      .then(([f, sf, p]) => {
        setForecast(f);
        setSkuForecasts(sf);
        setPmix(p);
      })
      .catch(console.error)
      .finally(() => setLoading(false));
  }, [selectedMonthId]);

  if (!selectedMonthId) {
    return (
      <>
        <PageHeader title="Tendencias y Proyecciones" subtitle="Forecast de demanda" />
        <EmptyState
          icon="trending_up"
          title="No hay mes seleccionado"
          description="Carga un archivo Excel para ver tendencias."
        />
      </>
    );
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center h-96">
        <span className="material-symbols-outlined text-5xl text-primary animate-spin">
          progress_activity
        </span>
      </div>
    );
  }

  const chartData = forecast.map((f) => ({
    date: f.date,
    composite: f.composite,
    lower: f.lower,
    upper: f.upper,
    range: [f.lower, f.upper] as [number, number],
  }));

  return (
    <>
      <PageHeader
        title="Tendencias y Proyecciones"
        subtitle="Pronóstico de demanda próximos 7 días"
      />

      <div className="grid grid-cols-12 gap-card-gap mb-card-gap">
        {/* Main forecast chart — glass floating */}
        <GlassCard variant="glass-strong" padding="lg" rounded="3xl" className="col-span-12 lg:col-span-8">
          <h3 className="font-headline-md text-headline-md text-on-surface mb-1">
            Forecast de Demanda (TUS)
          </h3>
          <p className="text-on-surface-variant opacity-80 text-body-md mb-6">
            Composite = MA7 (60%) + SDOW (40%) — banda de confianza ±1σ
          </p>
          <ResponsiveContainer width="100%" height={340}>
            <AreaChart data={chartData}>
              <defs>
                <linearGradient id="confBand" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="5%" stopColor="#ff8a65" stopOpacity={0.3} />
                  <stop offset="95%" stopColor="#ff8a65" stopOpacity={0.05} />
                </linearGradient>
              </defs>
              <CartesianGrid strokeDasharray="3 3" stroke="#2a2a2a" />
              <XAxis
                dataKey="date"
                stroke="#9ca3af"
                fontSize={12}
                tickFormatter={(d) => formatDate(d).split(' ')[0]}
                tickLine={false}
                axisLine={{ stroke: '#525252' }}
              />
              <YAxis
                stroke="#9ca3af"
                fontSize={12}
                tickLine={false}
                axisLine={{ stroke: '#525252' }}
              />
              <Tooltip
                contentStyle={{
                  background: '#0a0a0a',
                  border: '2px solid #ff8a65',
                  borderRadius: 0,
                  boxShadow: '4px 4px 0 0 #000000',
                  fontFamily: 'JetBrains Mono, monospace',
                  fontSize: '12px',
                  color: '#f5f5f5',
                }}
                labelStyle={{ color: '#ff8a65', fontWeight: 700 }}
                itemStyle={{ color: '#f5f5f5' }}
                formatter={(v: number) => [v.toFixed(1), '']}
                labelFormatter={(label) => formatDate(label as string)}
              />
              <Area type="monotone" dataKey="upper" stroke="none" fill="url(#confBand)" />
              <Area type="monotone" dataKey="lower" stroke="none" fill="#0a0a0a" fillOpacity={1} />
              <Line
                type="monotone"
                dataKey="composite"
                stroke="#ff8a65"
                strokeWidth={3}
                dot={{ fill: '#ff8a65', stroke: '#000', strokeWidth: 2, r: 4 }}
              />
            </AreaChart>
          </ResponsiveContainer>
          <div className="grid grid-cols-3 gap-4 mt-4 pt-4 neu-divider">
            <div>
              <p className="text-label-sm text-on-surface-variant opacity-70 uppercase tracking-wider">
                Promedio
              </p>
              <p className="font-headline-md text-headline-md text-on-surface num">
                {formatNumber(
                  forecast.reduce((s, f) => s + f.composite, 0) / (forecast.length || 1),
                )}
              </p>
            </div>
            <div>
              <p className="text-label-sm text-on-surface-variant opacity-70 uppercase tracking-wider">
                Máximo
              </p>
              <p className="font-headline-md text-headline-md text-on-surface num">
                {formatNumber(Math.max(...forecast.map((f) => f.composite)))}
              </p>
            </div>
            <div>
              <p className="text-label-sm text-on-surface-variant opacity-70 uppercase tracking-wider">
                Desv. Std
              </p>
              <p className="font-headline-md text-headline-md text-on-surface num">
                {forecast[0]?.stdDev.toFixed(1) || '—'}
              </p>
            </div>
          </div>
        </GlassCard>

        {/* SKU BCG matrix — neu card */}
        <GlassCard variant="neu" padding="lg" rounded="3xl" className="col-span-12 lg:col-span-4">
          <h3 className="font-headline-md text-headline-md text-on-surface mb-1">
            BCG Matrix (por Mix)
          </h3>
          <p className="text-on-surface-variant opacity-80 text-body-md mb-4">
            Cuota de mercado por SKU
          </p>
          <div className="space-y-2 max-h-96 overflow-y-auto custom-scrollbar">
            {pmix.map((p) => (
              <div
                key={p.code}
                className="flex items-center justify-between p-3 rounded-xl neu-card-sm cursor-pointer"
              >
                <div>
                  <p className="font-bold text-on-surface">
                    {p.code} · {p.name}
                  </p>
                  <p className="text-label-sm text-on-surface-variant opacity-70 num">
                    {formatNumber(p.units)} unidades · {p.participation.toFixed(1)}%
                  </p>
                </div>
                <SkuStatusBadge status={p.status} />
              </div>
            ))}
          </div>
        </GlassCard>
      </div>

      {/* SKU Forecasts — neu grid */}
      <GlassCard variant="neu-lg" padding="lg" rounded="3xl">
        <h3 className="font-headline-md text-headline-md text-on-surface mb-1">
          Forecast por SKU
        </h3>
        <p className="text-on-surface-variant opacity-80 text-body-md mb-6">
          Promedio de unidades diarias esperadas
        </p>
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
          {skuForecasts.map((s) => (
            <div
              key={s.code}
              className="neu-card-sm p-4 rounded-2xl cursor-pointer"
            >
              <p className="font-bold text-on-surface text-body-md">
                {s.code} · {s.name}
              </p>
              <p className="font-headline-md text-headline-md text-primary num mt-2">
                {s.historicalAvg.toFixed(1)}
              </p>
              <p className="text-label-sm text-on-surface-variant opacity-70">
                unid/día
              </p>
              <p className="text-label-sm text-on-surface-variant opacity-70 mt-1 num">
                7d: {formatNumber(s.forecastNext7.reduce((a, b) => a + b, 0))} unidades
              </p>
            </div>
          ))}
        </div>
      </GlassCard>
    </>
  );
}
